import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import CommonModal from "../../CommonComponents/Modal/CommonModal";
import CommonModalHeader from "../../CommonComponents/Modal/CommonModalHeader";
import CommonModalFooter from "../../CommonComponents/Modal/CommonModalFooter";
import {
  getCitiesByStateVendor,
  getStateListVendor,
  getSubProductsByLob,
} from "../../Services/vendorManagement.service";
import {
  getLocationErrorKey,
  getVendorSelectFields,
} from "../../Utils/addVendorModal.utils";
import { ADD_VENDOR_MODAL_TITLE } from "../../constants/addVendorModal.constants";
import {
  HIGH_VALUE_HEALTH,
  HIGH_VALUE_OD,
  HIGH_VALUE_TP,
  LOCATION_STEP,
  MOTOR_SUB_TO_HV_MAP,
  NO_HV,
  VENDOR_STEP,
  clubOptions,
  companySizeOptions,
  getInitialServiceLocations,
  getInitialVendorForm,
  healthHighValueOptions,
  healthSubProductOptions,
  lobPayloadMap,
  numericFieldNames,
  sanitizeEmail,
  sanitizeGstNumber,
  sanitizeMobileNumber,
  sanitizeNumber,
  sanitizePanNumber,
  subProductPayloadMap,
  toNumber,
  validateForm,
} from "../../Utils/helper";
import {
  buildModalSx,
  buildStepContentSx,
  buildSelectStylesBundle,
} from "./AddVendorModal.styles";
import styles from "./AddVendorModal.module.css";
import VendorDetailsStep from "./AddVendorModalComponent/VendorDetailsStep";
import ServiceLocationsStep from "./AddVendorModalComponent/ServiceLocationsStep";
import AddLocationBar from "./AddVendorModalComponent/AddLocationBar";

// ---------- constants ----------

const LOCATION_LOAD_ERROR = "Unable to load states. Please try again.";
const CITY_LOAD_ERROR = "Unable to load cities. Please try again.";
const FIELD_SANITIZERS = {
  pan_number: sanitizePanNumber,
  gst_number: sanitizeGstNumber,
  mobile_number: sanitizeMobileNumber,
  email: sanitizeEmail,
};

const AddVendorModal = ({
  open,
  onClose,
  onCreateVendor,
  onCreateVendorLocations,
  userRole,
  employeeId,
}) => {
  // FORM STATE

  const [activeStep, setActiveStep] = useState(VENDOR_STEP);
  const [vendorForm, setVendorForm] = useState(getInitialVendorForm());
  const [formErrors, setFormErrors] = useState({});
  // SERVICE LOCATIONS STATE
  const [serviceLocations, setServiceLocations] = useState(
    getInitialServiceLocations()
  );
  const [locationErrors, setLocationErrors] = useState({});
  // LOCATION MASTER DATA
  const [stateList, setStateList] = useState([]);
  const [cityCache, setCityCache] = useState({});
  const [locationLoadError, setLocationLoadError] = useState("");
  // SUB PRODUCT MASTER DATA
  const [subProductList, setSubProductList] = useState([]);
  // SUBMISSION STATE
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [vendorId, setVendorId] = useState(null);
  // DERIVED STATE
  const isLocationStep = activeStep === LOCATION_STEP;
  // Health always shows:
  // sub_products + capacity at service-location level.
  // Motor never shows these fields at service-location level.
  const isLocationAdvancedVisible = vendorForm.lob_id === "Health";
  const isMotor = vendorForm.lob_id === "Motor";
  const isHealth = vendorForm.lob_id === "Health";

  const defaultSelectStyles = useMemo(() => buildSelectStylesBundle(), []);
  // RESET WHEN MODAL OPENS
  useEffect(() => {
    if (!open) return;
    setActiveStep(VENDOR_STEP);
    setVendorForm(getInitialVendorForm());
    setServiceLocations(getInitialServiceLocations());
    setFormErrors({});
    setLocationErrors({});
    setLocationLoadError("");
    setVendorId(null);
    setCityCache({});
    setSubProductList([]);
  }, [open]);

  // FETCH STATE LIST WHEN MODAL OPENS
  useEffect(() => {
    if (!open) return;
    getStateListVendor()
      .then((res) => {
        if (res?.success) {
          setStateList(res.data || []);
        } else {
          setLocationLoadError(LOCATION_LOAD_ERROR);
        }
      })
      .catch(() => {
        setLocationLoadError(LOCATION_LOAD_ERROR);
      });
  }, [open]);

  // STATE / CITY HELPERS
  const stateOptions = useMemo(
    () => stateList.map((s) => s?.state_name).filter(Boolean),
    [stateList]
  );

  const getStateId = useCallback(
    (stateName) =>
      stateList.find((s) => s?.state_name === stateName)?.state_id || "",
    [stateList]
  );

  const getCityOptions = useCallback(
    (stateName) => {
      const stateId = getStateId(stateName);
      return stateId ? cityCache[stateId] || [] : [];
    },
    [getStateId, cityCache]
  );

  // FETCH CITIES FOR STATE
  const ensureCitiesForState = useCallback(
    async (stateName) => {
      const stateId = getStateId(stateName);

      // Already cached
      if (!stateId || cityCache[stateId]) {
        return;
      }
      try {
        const res = await getCitiesByStateVendor(stateId);
        if (res?.success) {
          setCityCache((prev) => ({
            ...prev,
            [stateId]: res.data || [],
          }));
        } else {
          setLocationLoadError(CITY_LOAD_ERROR);
        }
      } catch {
        setLocationLoadError(CITY_LOAD_ERROR);
      }
    },
    [getStateId, cityCache]
  );

  // SUB PRODUCT HELPERS
  const subProductOptions = useMemo(
    () => subProductList.map((sp) => sp?.sub_product_name).filter(Boolean),
    [subProductList]
  );

  // FETCH SUB PRODUCTS FOR LOB
  const fetchSubProductsForLob = useCallback(async (lobLabel) => {
    const lobId = lobPayloadMap[lobLabel];
    if (!lobId) {
      setSubProductList([]);
      return;
    }
    try {
      const res = await getSubProductsByLob(lobId);
      if (res?.success) {
        setSubProductList(res.data || []);
      } else {
        setSubProductList([]);
      }
    } catch {
      setSubProductList([]);
    }
  }, []);

  // SANITIZE FIELD VALUE
  const sanitizeFieldValue = (name, value) => {
    if (FIELD_SANITIZERS[name]) {
      return FIELD_SANITIZERS[name](value);
    }
    if (numericFieldNames.includes(name)) {
      return sanitizeNumber(value);
    }
    return value;
  };

  // MAP SUB PRODUCT LABELS TO IDS
  const mapSubProductLabelsToIds = (labels = []) => {
    const nameToId = new Map(
      subProductList.map((sp) => [sp?.sub_product_name, sp?.sub_product_id])
    );
    const ids = labels.flatMap((label) => {
      if (nameToId.has(label)) {
        return [nameToId.get(label)];
      }
      return subProductPayloadMap[label] || [];
    });
    return [...new Set(ids.filter((id) => id != null))].sort((a, b) => a - b);
  };

  // BUILD VENDOR PAYLOAD
  const buildVendorPayload = () => {
    const isMotor = vendorForm.lob_id === "Motor";
    const highValues = vendorForm.high_value || [];
    return {
      company_name: vendorForm.company_name,
      pan_number: vendorForm.pan_number,
      gc_code: vendorForm.gc_code,
      gst_number: vendorForm.gst_number,
      state: vendorForm.state,
      city: vendorForm.city,
      lob_id: lobPayloadMap[vendorForm.lob_id] || "",
      club: vendorForm.club,
      specialised: vendorForm.specialised === "yes" ? 1 : 0,
      company_size: vendorForm.company_size,
      // Motor only.
      // Health sub-products are handled
      // at service-location level.
      sub_product_id: isMotor
        ? mapSubProductLabelsToIds(vendorForm.sub_product_id)
        : [],
      mobile_number: vendorForm.mobile_number,
      email: vendorForm.email,
      capacity_motor: isMotor ? toNumber(vendorForm.capacity_motor) : 0,
      // High Value flags
      od_hv: highValues.includes(HIGH_VALUE_OD) ? 1 : 0,
      tp_hv: highValues.includes(HIGH_VALUE_TP) ? 1 : 0,
      health_hv: highValues.includes(HIGH_VALUE_HEALTH) ? 1 : 0,
      created_by: userRole,
      updated_by: userRole,
    };
  };

  // BUILD SERVICE LOCATIONS PAYLOAD
  const buildLocationsPayload = () => {
    const location = serviceLocations
      .map((row) => ({
        state: row.state,
        // Backend expects city as array.
        city: row.city ? [row.city] : [],
        per_week_capacity: isLocationAdvancedVisible
          ? toNumber(row.per_week_capacity)
          : 0,
        sub_products: isLocationAdvancedVisible
          ? mapSubProductLabelsToIds(row.sub_products || [])
          : [],
      }))
      .filter((entry) => entry.state?.trim() && entry.city.length);
    return {
      vendor_company_id: vendorId,
      employee_id: employeeId,
      location,
    };
  };

  // ERROR HELPERS
  const clearFieldErrors = (fields) =>
    setFormErrors((prev) => {
      const names = Array.isArray(fields) ? fields : [fields];

      const next = {
        ...prev,
      };

      names.forEach((name) => {
        delete next[name];
      });

      return next;
    });
  const clearLocationError = (index, field) => {
    const key = getLocationErrorKey(index, field);
    setLocationErrors((prev) => {
      if (!prev[key]) {
        return prev;
      }
      const next = {
        ...prev,
      };
      delete next[key];
      return next;
    });
  };

  // VALIDATE SERVICE LOCATIONS
  const validateServiceLocations = (locations = serviceLocations) => {
    const errors = {};
    locations.forEach((location, index) => {
      if (!location?.state) {
        errors[getLocationErrorKey(index, "state")] = "State is required.";
      }
      if (!location?.city) {
        errors[getLocationErrorKey(index, "city")] = "City is required.";
      }
      if (isLocationAdvancedVisible) {
        if (!location?.sub_products?.length) {
          errors[getLocationErrorKey(index, "sub_products")] =
            "Sub Product ID is required.";
        }
        if (!String(location?.per_week_capacity || "").trim()) {
          errors[getLocationErrorKey(index, "per_week_capacity")] =
            "Capacity is required.";
        }
      }
    });
    setLocationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // VENDOR FORM HANDLERS
  const handleChange = (event) => {
    const { name } = event.target;
    const value = sanitizeFieldValue(name, event.target.value);
    clearFieldErrors(name);
    setVendorForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // VENDOR STATE CHANGE
  const handleVendorStateChange = (event) => {
    const value = event.target.value;
    clearFieldErrors(["state", "city"]);
    setVendorForm((prev) => ({
      ...prev,
      state: value,
      city: "",
    }));
    if (value) {
      ensureCitiesForState(value);
    }
  };

  // LOB CHANGE
  const handleLobChange = (event) => {
    const selected = event.target.value;
    clearFieldErrors("lob_id");
    clearFieldErrors(["capacity_motor", "sub_product_id", "high_value"]);
    setLocationErrors({});
    setVendorForm((prev) => ({
      ...prev,
      lob_id: selected,
      sub_product_id: [],
      capacity_motor: selected === "Motor" ? prev.capacity_motor : "",
      high_value: [],
    }));

    // Clear stale Health location values.
    setServiceLocations((prev) =>
      prev.map((loc) => ({
        ...loc,
        sub_products: [],
        per_week_capacity: "",
      }))
    );
    setSubProductList([]);
    fetchSubProductsForLob(selected);
  };

  // MOTOR SUB PRODUCT CHANGE
  const handleSubProductChange = (event) => {
    const raw = event.target.value;
    const nextSubProducts = typeof raw === "string" ? raw.split(",") : raw;
    clearFieldErrors("sub_product_id");
    clearFieldErrors("high_value");
    const availableHv = new Set(
      nextSubProducts.map((sp) => MOTOR_SUB_TO_HV_MAP[sp]).filter(Boolean)
    );
    if (nextSubProducts.length > 0) {
      availableHv.add(NO_HV);
    }
    setVendorForm((prev) => ({
      ...prev,
      sub_product_id: nextSubProducts,
      high_value: prev.high_value.filter((hv) => availableHv.has(hv)),
    }));
  };

  // HIGH VALUE CHANGE
  const handleHighValueChange = (event, availableOptions) => {
    const raw = event.target.value;
    const rawValues = typeof raw === "string" ? raw.split(",") : raw;
    const prevValues = vendorForm.high_value;
    clearFieldErrors("high_value");
    const added = rawValues.filter((v) => !prevValues.includes(v));
    const noHvJustAdded = added.includes(NO_HV);
    const hvJustAdded = added.some((v) => v !== NO_HV);
    let nextValues = rawValues.filter((v) => availableOptions.includes(v));
    if (noHvJustAdded) {
      nextValues = [NO_HV];
    } else if (hvJustAdded) {
      nextValues = nextValues.filter((v) => v !== NO_HV);
    }
    setVendorForm((prev) => ({
      ...prev,
      high_value: nextValues,
    }));
  };

  // SERVICE LOCATION HANDLERS
  const handleLocationChange = (index, field, value) => {
    clearLocationError(index, field);
    if (field === "state") {
      clearLocationError(index, "city");
    }
    const nextValue =
      field === "per_week_capacity" ? sanitizeNumber(value) : value;
    setServiceLocations((prev) =>
      prev.map((loc, i) =>
        i !== index
          ? loc
          : {
              ...loc,

              [field]: nextValue,
              // Changing state
              // resets city.
              ...(field === "state"
                ? {
                    city: "",
                  }
                : {}),
            }
      )
    );

    // Fetch cities for new state.
    if (field === "state" && value) {
      ensureCitiesForState(value);
    }
  };

  // ADD LOCATION
  const handleAddLocation = () => {
    if (!validateServiceLocations()) {
      return;
    }
    setServiceLocations((prev) => [
      ...prev,
      {
        state: "",
        city: "",
        sub_products: [],
        per_week_capacity: "",
      },
    ]);
  };

  // REMOVE LOCATION
  const handleRemoveLocation = (index) => {
    setServiceLocations((prev) => prev.filter((_, i) => i !== index));
    setLocationErrors({});
  };

  // CLOSE MODAL
  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    setActiveStep(VENDOR_STEP);
    setVendorForm(getInitialVendorForm());
    setServiceLocations(getInitialServiceLocations());
    setFormErrors({});
    setLocationErrors({});
    setVendorId(null);
    onClose();
  };

  // NEXT - VENDOR -> LOCATION
  const handleNext = async () => {
    const errors = validateForm(vendorForm);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    // If vendor already created
    // or create callback isn't available,
    // directly move to location step.
    if (vendorId || typeof onCreateVendor !== "function") {
      setFormErrors({});
      setActiveStep(LOCATION_STEP);
      return;
    }
    try {
      setIsSubmitting(true);
      const result = await onCreateVendor(buildVendorPayload());
      if (!result?.success) {
        return;
      }
      setVendorId(result.vendorId ?? null);
      setFormErrors({});
      setActiveStep(LOCATION_STEP);
    } finally {
      setIsSubmitting(false);
    }
  };

  // FINAL SUBMIT
  const handleSubmit = async () => {
    if (!validateServiceLocations()) {
      return;
    }
    try {
      setIsSubmitting(true);

      const isSubmitted = await onCreateVendorLocations(
        buildLocationsPayload()
      );
      if (isSubmitted !== false) {
        handleClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // MOTOR HIGH VALUE OPTIONS
  const motorHighValueOptions = useMemo(() => {
    const opts = [];
    vendorForm.sub_product_id.forEach((sp) => {
      const hv = MOTOR_SUB_TO_HV_MAP[sp];

      if (hv && !opts.includes(hv)) {
        opts.push(hv);
      }
    });
    if (vendorForm.sub_product_id.length > 0) {
      opts.push(NO_HV);
    }
    return opts;
  }, [vendorForm.sub_product_id]);

  // HIGH VALUE OPTIONS
  const highValueOptions = isMotor
    ? motorHighValueOptions
    : isHealth
      ? healthHighValueOptions
      : [];

  // VENDOR SELECT FIELDS
  const vendorSelectFields = useMemo(
    () =>
      getVendorSelectFields({
        stateOptions,
        cityOptions: getCityOptions(vendorForm.state),
        clubOptions,
        companySizeOptions,
        isCityDisabled: !vendorForm.state,
      }),
    [stateOptions, getCityOptions, vendorForm.state]
  );

  // RENDER
  return (
    <CommonModal
      open={open}
      label=""
      onClose={handleClose}
      width="96vw"
      maxWidth="980px"
      showCrossIcon={false}
      sx={buildModalSx(isLocationStep)}
    >
      <Box className={styles.modalBody}>
        <CommonModalHeader
          activeStep={activeStep}
          locationStep={LOCATION_STEP}
          title={ADD_VENDOR_MODAL_TITLE}
          headerSx={{
            alignItems: "center",
            borderBottom: "1px solid #EEEEEE",
            display: "flex",
            gap: "0.8rem",
          }}
        />

        <Box
          className={styles.stepContent}
          sx={buildStepContentSx(isLocationStep)}
        >
          {activeStep === VENDOR_STEP ? (
            <VendorDetailsStep
              vendorForm={vendorForm}
              formErrors={formErrors}
              vendorSelectFields={vendorSelectFields}
              subProductOptions={subProductOptions}
              highValueOptions={highValueOptions}
              isMotor={isMotor}
              isHealth={isHealth}
              locationLoadError={locationLoadError}
              onChange={handleChange}
              onStateChange={handleVendorStateChange}
              onLobChange={handleLobChange}
              onSubProductChange={handleSubProductChange}
              onHighValueChange={handleHighValueChange}
              defaultSelectStyles={defaultSelectStyles}
            />
          ) : (
            <ServiceLocationsStep
              serviceLocations={serviceLocations}
              stateOptions={stateOptions}
              getCityOptions={getCityOptions}
              subProductOptions={
                subProductOptions.length
                  ? subProductOptions
                  : healthSubProductOptions
              }
              showAdvanced={isLocationAdvancedVisible}
              locationErrors={locationErrors}
              locationLoadError={locationLoadError}
              onFieldChange={handleLocationChange}
              onRemove={handleRemoveLocation}
            />
          )}
        </Box>
        {isLocationStep && <AddLocationBar onAddLocation={handleAddLocation} />}
        <CommonModalFooter
          activeStep={activeStep}
          vendorStep={VENDOR_STEP}
          locationStep={LOCATION_STEP}
          isSubmitting={isSubmitting}
          onCancelOrBack={handleClose}
          onPrimary={activeStep === VENDOR_STEP ? handleNext : handleSubmit}
        />
      </Box>
    </CommonModal>
  );
};

export default AddVendorModal;
